import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Tabs,
  Tab,
  Grid,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  LocalShipping as ShippingIcon,
  Inventory as InventoryIcon,
} from '@mui/icons-material';
import { simpleApi, Product, ProductCreate } from '../../services/simpleApi';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`product-tabpanel-${index}`}
      aria-labelledby={`product-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const emptyProduct: ProductCreate = {
  name: '',
  sku: '',
  category: '',
  price: 0,
  cost: 0,
  landed_cost: 0,
  stock: 0,
  safety_stock: 0,
  reorder_point: 0,
  reorder_quantity: 0,
  max_stock: 0,
  warehouse_id: '',
  bin_location: '',
  unit_of_measure: 'each',
  weight_kg: 0,
  length_cm: 0,
  width_cm: 0,
  height_cm: 0,
  is_hazardous: false,
  storage_conditions: '',
  primary_supplier_id: '',
  supplier_sku: '',
  supplier_lead_time_days: 0,
  avg_daily_demand: 0,
  lot_tracking_enabled: false,
  serial_tracking_enabled: false,
  expiration_tracking: false,
  shelf_life_days: undefined,
  country_of_origin: '',
  hs_code: '',
  quality_grade: '',
  certifications: '',
  status: 'active',
  description: '',
};

const ProductsPage: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<ProductCreate>(emptyProduct);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  const fetchProducts = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await simpleApi.products.list();
      setProducts(data.items || []);
    } catch (err) {
      setError('Failed to fetch products');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleOpenDialog = (product?: Product) => {
    if (product) {
      setEditingId(product.id);
      setEditingProduct({
        name: product.name,
        sku: product.sku,
        category: product.category || '',
        price: product.price || 0,
        cost: product.cost || 0,
        landed_cost: product.landed_cost || 0,
        stock: product.stock || 0,
        safety_stock: product.safety_stock || 0,
        reorder_point: product.reorder_point || 0,
        reorder_quantity: product.reorder_quantity || 0,
        max_stock: product.max_stock || 0,
        warehouse_id: product.warehouse_id || '',
        bin_location: product.bin_location || '',
        unit_of_measure: product.unit_of_measure || 'each',
        weight_kg: product.weight_kg || 0,
        length_cm: product.length_cm || 0,
        width_cm: product.width_cm || 0,
        height_cm: product.height_cm || 0,
        is_hazardous: product.is_hazardous || false,
        storage_conditions: product.storage_conditions || '',
        primary_supplier_id: product.primary_supplier_id || '',
        supplier_sku: product.supplier_sku || '',
        supplier_lead_time_days: product.supplier_lead_time_days || 0,
        avg_daily_demand: product.avg_daily_demand || 0,
        lot_tracking_enabled: product.lot_tracking_enabled || false,
        serial_tracking_enabled: product.serial_tracking_enabled || false,
        expiration_tracking: product.expiration_tracking || false,
        shelf_life_days: product.shelf_life_days,
        country_of_origin: product.country_of_origin || '',
        hs_code: product.hs_code || '',
        quality_grade: product.quality_grade || '',
        certifications: product.certifications || '',
        status: product.status || 'active',
        description: product.description || '',
      });
    } else {
      setEditingId(null);
      setEditingProduct(emptyProduct);
    }
    setTabValue(0);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingProduct(emptyProduct);
    setEditingId(null);
    setTabValue(0);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingId) {
        await simpleApi.products.update(editingId, editingProduct);
      } else {
        await simpleApi.products.create(editingProduct);
      }
      handleCloseDialog();
      fetchProducts();
    } catch (err) {
      setError('Failed to save product');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await simpleApi.products.delete(id);
      fetchProducts();
    } catch (err) {
      setError('Failed to delete product');
      console.error(err);
    }
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'active': return 'success';
      case 'low_stock': return 'warning';
      case 'out_of_stock': return 'error';
      case 'discontinued': return 'default';
      default: return 'default';
    }
  };

  const getStockStatus = (product: Product): { label: string; color: 'success' | 'warning' | 'error' } => {
    const stock = product.stock || 0;
    const safetyStock = product.safety_stock || 0;
    const reorderPoint = product.reorder_point || 0;
    
    if (stock === 0) return { label: 'Out of Stock', color: 'error' };
    if (stock <= safetyStock) return { label: 'Critical', color: 'error' };
    if (stock <= reorderPoint) return { label: 'Low Stock', color: 'warning' };
    return { label: 'In Stock', color: 'success' };
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InventoryIcon /> Products
        </Typography>
        <Box>
          <Button
            startIcon={<RefreshIcon />}
            onClick={fetchProducts}
            sx={{ mr: 1 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Product
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>SKU</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell align="right">Price</TableCell>
                <TableCell align="right">Cost</TableCell>
                <TableCell align="right">Margin</TableCell>
                <TableCell align="right">Stock</TableCell>
                <TableCell>Stock Status</TableCell>
                <TableCell>Location</TableCell>
                <TableCell align="right">Lead Time</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {products.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} align="center">
                    No products found. Add your first product to get started.
                  </TableCell>
                </TableRow>
              ) : (
                products.map((product) => {
                  const stockStatus = getStockStatus(product);
                  const margin = product.price && product.cost 
                    ? ((product.price - product.cost) / product.price * 100).toFixed(1)
                    : '-';
                  return (
                    <TableRow key={product.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {product.sku}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {product.name}
                          </Typography>
                          {product.is_hazardous && (
                            <Chip
                              icon={<WarningIcon />}
                              label="Hazardous"
                              size="small"
                              color="warning"
                              sx={{ mt: 0.5 }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{product.category || '-'}</TableCell>
                      <TableCell align="right">${product.price?.toFixed(2) || '0.00'}</TableCell>
                      <TableCell align="right">${product.cost?.toFixed(2) || '0.00'}</TableCell>
                      <TableCell align="right">{margin}%</TableCell>
                      <TableCell align="right">
                        <Tooltip title={`Safety: ${product.safety_stock || 0} | Reorder: ${product.reorder_point || 0}`}>
                          <span>{product.stock || 0} {product.unit_of_measure || 'each'}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={stockStatus.label}
                          color={stockStatus.color}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {product.bin_location || '-'}
                      </TableCell>
                      <TableCell align="right">
                        {product.supplier_lead_time_days ? (
                          <Tooltip title="Lead time from supplier">
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                              <ShippingIcon fontSize="small" color="action" />
                              {product.supplier_lead_time_days}d
                            </Box>
                          </Tooltip>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={product.status}
                          color={getStatusColor(product.status || 'active')}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(product)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(product.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Enhanced Product Dialog with Tabs */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingId ? 'Edit Product' : 'Add Product'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
              <Tab label="Basic Info" />
              <Tab label="Inventory" />
              <Tab label="Physical" />
              <Tab label="Supplier" />
              <Tab label="Demand" />
              <Tab label="Compliance" />
            </Tabs>
          </Box>

          {/* Tab 0: Basic Info */}
          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Product Name"
                  value={editingProduct.name}
                  onChange={(e) => setEditingProduct({ ...editingProduct, name: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="SKU"
                  value={editingProduct.sku}
                  onChange={(e) => setEditingProduct({ ...editingProduct, sku: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Category"
                  value={editingProduct.category}
                  onChange={(e) => setEditingProduct({ ...editingProduct, category: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={editingProduct.status}
                    label="Status"
                    onChange={(e) => setEditingProduct({ ...editingProduct, status: e.target.value })}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="low_stock">Low Stock</MenuItem>
                    <MenuItem value="out_of_stock">Out of Stock</MenuItem>
                    <MenuItem value="discontinued">Discontinued</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Price"
                  type="number"
                  value={editingProduct.price}
                  onChange={(e) => setEditingProduct({ ...editingProduct, price: parseFloat(e.target.value) || 0 })}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Cost"
                  type="number"
                  value={editingProduct.cost}
                  onChange={(e) => setEditingProduct({ ...editingProduct, cost: parseFloat(e.target.value) || 0 })}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Landed Cost"
                  type="number"
                  value={editingProduct.landed_cost}
                  onChange={(e) => setEditingProduct({ ...editingProduct, landed_cost: parseFloat(e.target.value) || 0 })}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  helperText="Total cost incl. shipping, duties"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  value={editingProduct.description}
                  onChange={(e) => setEditingProduct({ ...editingProduct, description: e.target.value })}
                />
              </Grid>
            </Grid>
          </TabPanel>

          {/* Tab 1: Inventory */}
          <TabPanel value={tabValue} index={1}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Current Stock"
                  type="number"
                  value={editingProduct.stock}
                  onChange={(e) => setEditingProduct({ ...editingProduct, stock: parseInt(e.target.value) || 0 })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Unit of Measure"
                  value={editingProduct.unit_of_measure}
                  onChange={(e) => setEditingProduct({ ...editingProduct, unit_of_measure: e.target.value })}
                  helperText="e.g., each, box, kg, liter"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Safety Stock"
                  type="number"
                  value={editingProduct.safety_stock}
                  onChange={(e) => setEditingProduct({ ...editingProduct, safety_stock: parseInt(e.target.value) || 0 })}
                  helperText="Emergency buffer level"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Max Stock"
                  type="number"
                  value={editingProduct.max_stock}
                  onChange={(e) => setEditingProduct({ ...editingProduct, max_stock: parseInt(e.target.value) || 0 })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Reorder Point"
                  type="number"
                  value={editingProduct.reorder_point}
                  onChange={(e) => setEditingProduct({ ...editingProduct, reorder_point: parseInt(e.target.value) || 0 })}
                  helperText="Stock level to trigger reorder"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Reorder Quantity"
                  type="number"
                  value={editingProduct.reorder_quantity}
                  onChange={(e) => setEditingProduct({ ...editingProduct, reorder_quantity: parseInt(e.target.value) || 0 })}
                  helperText="Economic order quantity"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Warehouse ID"
                  value={editingProduct.warehouse_id || ''}
                  onChange={(e) => setEditingProduct({ ...editingProduct, warehouse_id: e.target.value || '' })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Bin Location"
                  value={editingProduct.bin_location}
                  onChange={(e) => setEditingProduct({ ...editingProduct, bin_location: e.target.value })}
                  helperText="e.g., A-01-03"
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={editingProduct.lot_tracking_enabled}
                        onChange={(e) => setEditingProduct({ ...editingProduct, lot_tracking_enabled: e.target.checked })}
                      />
                    }
                    label="Lot Tracking"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={editingProduct.serial_tracking_enabled}
                        onChange={(e) => setEditingProduct({ ...editingProduct, serial_tracking_enabled: e.target.checked })}
                      />
                    }
                    label="Serial Tracking"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={editingProduct.expiration_tracking}
                        onChange={(e) => setEditingProduct({ ...editingProduct, expiration_tracking: e.target.checked })}
                      />
                    }
                    label="Expiration Tracking"
                  />
                </Box>
              </Grid>
              {editingProduct.expiration_tracking && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Shelf Life (Days)"
                    type="number"
                    value={editingProduct.shelf_life_days || ''}
                    onChange={(e) => setEditingProduct({ ...editingProduct, shelf_life_days: parseInt(e.target.value) || undefined })}
                  />
                </Grid>
              )}
            </Grid>
          </TabPanel>

          {/* Tab 2: Physical */}
          <TabPanel value={tabValue} index={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Weight (kg)"
                  type="number"
                  value={editingProduct.weight_kg}
                  onChange={(e) => setEditingProduct({ ...editingProduct, weight_kg: parseFloat(e.target.value) || 0 })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Alert severity="info" sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                  Weight in kilograms
                </Alert>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Length (cm)"
                  type="number"
                  value={editingProduct.length_cm}
                  onChange={(e) => setEditingProduct({ ...editingProduct, length_cm: parseFloat(e.target.value) || 0 })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Width (cm)"
                  type="number"
                  value={editingProduct.width_cm}
                  onChange={(e) => setEditingProduct({ ...editingProduct, width_cm: parseFloat(e.target.value) || 0 })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Height (cm)"
                  type="number"
                  value={editingProduct.height_cm}
                  onChange={(e) => setEditingProduct({ ...editingProduct, height_cm: parseFloat(e.target.value) || 0 })}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editingProduct.is_hazardous}
                      onChange={(e) => setEditingProduct({ ...editingProduct, is_hazardous: e.target.checked })}
                    />
                  }
                  label="Hazardous Material"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Storage Conditions"
                  multiline
                  rows={2}
                  value={editingProduct.storage_conditions}
                  onChange={(e) => setEditingProduct({ ...editingProduct, storage_conditions: e.target.value })}
                  helperText="e.g., Keep refrigerated, Store away from heat"
                />
              </Grid>
            </Grid>
          </TabPanel>

          {/* Tab 3: Supplier */}
          <TabPanel value={tabValue} index={3}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Primary Supplier ID"
                  value={editingProduct.primary_supplier_id || ''}
                  onChange={(e) => setEditingProduct({ ...editingProduct, primary_supplier_id: e.target.value || '' })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Supplier SKU"
                  value={editingProduct.supplier_sku}
                  onChange={(e) => setEditingProduct({ ...editingProduct, supplier_sku: e.target.value })}
                  helperText="SKU used by supplier"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Lead Time (Days)"
                  type="number"
                  value={editingProduct.supplier_lead_time_days}
                  onChange={(e) => setEditingProduct({ ...editingProduct, supplier_lead_time_days: parseInt(e.target.value) || 0 })}
                  helperText="Supplier delivery time"
                />
              </Grid>
            </Grid>
          </TabPanel>

          {/* Tab 4: Demand */}
          <TabPanel value={tabValue} index={4}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12}>
                <TextField
                  fullWidth
                  label="Avg Daily Demand"
                  type="number"
                  value={editingProduct.avg_daily_demand}
                  onChange={(e) => setEditingProduct({ ...editingProduct, avg_daily_demand: parseFloat(e.target.value) || 0 })}
                  helperText="Units sold per day on average"
                />
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info">
                  Demand metrics are used for forecasting reorder quantities and safety stock calculations.
                  Additional demand forecasting data is available through the AI agents.
                </Alert>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Tab 5: Compliance */}
          <TabPanel value={tabValue} index={5}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Country of Origin"
                  value={editingProduct.country_of_origin}
                  onChange={(e) => setEditingProduct({ ...editingProduct, country_of_origin: e.target.value })}
                  helperText="ISO country code (e.g., US, CN, DE)"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="HS Code"
                  value={editingProduct.hs_code}
                  onChange={(e) => setEditingProduct({ ...editingProduct, hs_code: e.target.value })}
                  helperText="Harmonized System tariff code"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Quality Grade"
                  value={editingProduct.quality_grade}
                  onChange={(e) => setEditingProduct({ ...editingProduct, quality_grade: e.target.value })}
                  helperText="e.g., A, B, Premium"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Certifications"
                  value={editingProduct.certifications}
                  onChange={(e) => setEditingProduct({ ...editingProduct, certifications: e.target.value })}
                  helperText="e.g., ISO9001, CE, UL"
                />
              </Grid>
            </Grid>
          </TabPanel>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !editingProduct.name || !editingProduct.sku}
          >
            {saving ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductsPage;
